import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// ======================================================
// GEO LOCATION SEARCH CONFIGURATION
// ======================================================
// Examples:
// OSM     → https://nominatim.openstreetmap.org/search
// Google  → https://maps.googleapis.com/maps/api/geocode/json

const GEO_SEARCH_URL = "https://nominatim.openstreetmap.org/search";

// ======================================================
// GOOGLE MAPS (OPTIONAL – REQUIRED ONLY IF USING GOOGLE)
// ======================================================
// Required only when VITE_GEO_SEARCH_URL points to Google.
// Safe to keep empty when using OSM.

const GOOGLE_API_KEY = "";

export interface SearchResult {
  lat: number;
  lon: number;
  name: string;
  display_name?: string;
}

export const buildSearchUrl = (baseUrl: string, query: string): string => {
  if (baseUrl.includes("googleapis")) {
    return `${baseUrl}?address=${encodeURIComponent(
      query
    )}&key=${GOOGLE_API_KEY}`;
  }
  return `${baseUrl}?q=${encodeURIComponent(query)}&format=json&limit=5`;
};

export const parseSearchResponse = (
  baseUrl: string,
  data: any
): SearchResult[] => {
  if (baseUrl.includes("googleapis")) {
    return data.results.map((item: any) => ({
      lat: item.geometry.location.lat,
      lon: item.geometry.location.lng,
      name: item.formatted_address,
    }));
  }

  return data.map((item: any) => ({
    lat: Number(item.lat),
    lon: Number(item.lon),
    name: item.name,
    display_name: item.display_name,
  }));
};

export const searchLocationApi = createApi({
  reducerPath: "searchLocationApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/" }),
  endpoints: (builder) => ({
    searchLocation: builder.query<SearchResult[], { query: string }>({
      query: ({ query }) => buildSearchUrl(GEO_SEARCH_URL, query),
      transformResponse: (response: any) =>
        parseSearchResponse(GEO_SEARCH_URL, response),
    }),
  }),
});

export const { useSearchLocationQuery } = searchLocationApi;
